import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SitterChat from "./SitterChat";
import "./sitter-page.css";

const API_BASE = "http://localhost:4000";
//const API_BASE = "https://myjourneytrails.com";

export default function SitterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [liveStatus, setLiveStatus] = useState("Locked");
  const [ending, setEnding] = useState(false);
  const [isJournaler, setIsJournaler] = useState(false);
  const [journalerName, setJournalerName] = useState("Journaler");
  const [sitterName, setSitterName] = useState("Sitter");
  const [accessCode, setAccessCode] = useState("");
  const [accessError, setAccessError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [initialUserCheckDone, setInitialUserCheckDone] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/me`, { credentials: "include" })
      .then(res => res.ok ? res.json() : null)
      .then(user => {
        setIsJournaler(!!user);
        setInitialUserCheckDone(true);
      })
      .catch(() => {
        setIsJournaler(false);
        setInitialUserCheckDone(true);
      });
  }, []);

  useEffect(() => {
    if (!token) return;
    const saved = sessionStorage.getItem(`sitter_access_${token}`);
    if (saved === '1') setVerified(true);
  }, [token]);

  useEffect(() => {
    if (!token || (!isJournaler && !verified)) return;
    let active = true;
    async function loadSession() {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(`${API_BASE}/api/sitter/${token}`);
        const data = await res.json();
        if (!active) return;
        if (res.status === 404) throw new Error(data.error || "Session not found");
        if (res.status === 403) throw new Error(data.error || "Access code required");
        setSession(data.session);
        setMessages(data.messages || []);
        const ended = !!data.session?.ended_at || data.status === "expired" || data.session?.status === "expired";
        setLiveStatus(ended ? "Session ended" : "Live");
        const name = data.session?.display_name || data.session?.user_email?.split("@")[0] || "Journaler";
        setJournalerName(name);
        setSitterName(data.session?.sitter_name || "Companion");
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load session");
      } finally {
        if (active) setLoading(false);
      }
    }
    loadSession();
    return () => { active = false; };
  }, [token, verified, isJournaler]);

  useEffect(() => {
    if (!token || (!isJournaler && !verified)) return;
    let cancelled = false;
    let socket;
    async function connectSocket() {
      try {
        const mod = await import("socket.io-client");
        if (cancelled) return;
        const io = mod.io || mod.default;
        //FOR LOCAL:
        //socket = io(API_BASE, { transports: ["websocket"] });
        //FOR PRODUCTION:
        socket = io(API_BASE, {
          path: '/backend/socket.io',
          transports: ["websocket", "polling"]
        });
        socketRef.current = socket;
        socket.on("connect", () => { socket.emit("join_session", token); setLiveStatus(prev => prev === "Locked" ? "Live" : prev); });
        socket.on("disconnect", () => setLiveStatus("Reconnecting…"));
        socket.on("new_message", (msg) => { setMessages(prev => { const exists = prev.some(m => m.sent_at === msg.sent_at && m.sender === msg.sender && m.message === msg.message); return exists ? prev : [...prev, msg]; }); });
        socket.on("messages_cleared", ({ sender }) => { setMessages(prev => prev.filter(m => m.sender !== sender)); });
        socket.on("session_ended", () => { setLiveStatus("Session ended"); setSession(prev => prev ? { ...prev, ended_at: prev.ended_at || new Date().toISOString() } : prev); });
      } catch {}
    }
    connectSocket();
    return () => { cancelled = true; socketRef.current = null; if (socket) socket.disconnect(); };
  }, [token, verified, isJournaler]);

  const canEndSession = useMemo(() => !!session && !session.ended_at, [session]);
  const sessionEnded = !!session?.ended_at;
  const disableChat = isJournaler;
  const showAccessGate = initialUserCheckDone && !isJournaler && !verified;
console.log({ initialUserCheckDone, isJournaler, verified, showAccessGate });
  async function submitAccessCode(e) {
    e.preventDefault();
    try {
      setVerifying(true);
      setAccessError("");
      const res = await fetch(`${API_BASE}/api/sitter/${token}/request-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Invalid access code");
      sessionStorage.setItem(`supporter_access_${token}`, '1');
      setVerified(true);
    } catch (err) {
      setAccessError(err.message || "Invalid access code");
    } finally {
      setVerifying(false);
    }
  }

  async function handleEndSession() {
    try {
      setEnding(true);
      const res = await fetch(`${API_BASE}/api/sitter/${token}/end`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to end session");
      setLiveStatus("Session ended");
      setSession(prev => prev ? { ...prev, ended_at: new Date().toISOString() } : prev);
    } catch (err) { setError(err.message || "Failed to load session"); }
    finally { setEnding(false); }
  }

  if (!initialUserCheckDone) {
    return <div className="sitter-shell"><div className="sitter-card">Loading session…</div></div>;
  }

  if (showAccessGate) {
    return (
      <div className="sitter-shell">
        <main className="sitter-card" style={{ maxWidth: 520 }}>
          <header className="sitter-header">
            <div>
              <h1>🧭 Companion Session</h1>
              <p className="sitter-subtitle">Enter the access code from your invite email to open the session.</p>
            </div>
          </header>
          <form onSubmit={submitAccessCode} style={{ display: 'grid', gap: 12 }}>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 700 }}>Access Code:</span>
              <input value={accessCode} onChange={(e) => setAccessCode(e.target.value.toUpperCase())} maxLength={6} autoComplete="one-time-code" inputMode="text" style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff', letterSpacing: '0.12em' }} />
            </label>
            {accessError && <div style={{ color: '#ff9da8', fontSize: 13 }}>{accessError}</div>}
            <button className="session-btn" type="submit" disabled={verifying || accessCode.length < 6}>{verifying ? 'Checking…' : 'Open Session'}</button>
          </form>
        </main>
      </div>
    );
  }

  if (loading) return <div className="sitter-shell"><div className="sitter-card">Loading session…</div></div>;
  if (error) return <div className="sitter-shell"><div className="sitter-card sitter-error"><h1>Session unavailable</h1><p>{error}</p></div></div>;

  return (
    <div className="sitter-shell">
      <main className="sitter-card">
        <header className="sitter-header">
          <div>
            <h1>🧭 Companion Session</h1>
            <p className="sitter-subtitle">{isJournaler ? "Your companion can see this page and send you notes." : `Joining ${journalerName} for their session.`}</p>
          </div>
          <div className={`sitter-status ${sessionEnded ? "ended" : "live"}`}>{liveStatus}</div>
        </header>
        {sessionEnded && isJournaler && (
  <div
    style={{
      marginBottom: 24,
      padding: 14,
      borderRadius: 12,
      border: '1px solid rgba(180,125,232,0.3)',
      background: 'rgba(180,125,232,0.07)',
      fontSize: 13,
      opacity: 0.85
    }}
  >
    🌀 Your session flow has ended.{" "}
    {session?.history_was_preserved
      ? "The chat log you see below was preserved as your transcript."
      : "The chat log below was not preserved and will not appear in your journal entry."}
  </div>
)}


        <section className="sitter-grid">
          <div className="sitter-panel chat-panel">
            <div className="sitter-panel-head">
              <h2 style={{ margin: 0, flexShrink: 0 }}>Live Notes</h2>
              <p className="sitter-muted">Send short notes, check in, or capture what is happening now.</p>
              {isJournaler && canEndSession && (<button className="sitter-end-btn" onClick={handleEndSession} disabled={ending}>{ending ? "Ending…" : "End Session"}</button>)}
            </div>
            <SitterChat token={token} messages={messages} setMessages={setMessages} sessionEnded={sessionEnded} isJournaler={isJournaler} journalerName={journalerName} sitterName={sitterName} disableChat={disableChat} />
          </div>
          <div className="sitter-panel">
            <h2>Session Details</h2>
            {session?.user_note && <div className="sitter-user-note"><div className="sitter-user-note-label">Note from {journalerName}</div><p>{session.user_note}</p></div>}
            <dl className="sitter-dl">
              <div><dt>Journaler</dt><dd>{journalerName}</dd></div>
              <div><dt>Journey</dt><dd>{session?.medicine_type || "-"}</dd></div>
              {session?.subcategory && <div><dt>Type</dt><dd>{session.subcategory}</dd></div>}
              {session?.amount && <div><dt>Amount</dt><dd>{session.amount}</dd></div>}
              {session?.method && <div><dt>Form</dt><dd>{session.method}</dd></div>}
              {session?.session_timing && <div><dt>Set & Setting</dt><dd>{session.session_timing}</dd></div>}
              {session?.intention && <div><dt>Intention</dt><dd>{session.intention}</dd></div>}
            </dl>
            {isJournaler && <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}><button type="button" onClick={() => window.open(`/new?sitter_token=${token}`, "_blank")} style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(125,211,232,0.3)', background: 'rgba(125,211,232,0.08)', color: '#7dd3e8', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>📓 Write Journal Entry ↗</button><p style={{ fontSize: 11, opacity: 0.45, marginTop: 6, textAlign: 'center', lineHeight: 1.5 }}>Opens in a new tab. Your session details will be pre-filled.</p></div>}
          </div>
        </section>
      </main>
    </div>
  );
}
